
import { Ball, SimulationConfig, Vector2D } from '@/types';
import { AUDIO } from '@/services/AudioService';

export class SpectrumLogic {
  public static update(
    deltaTime: number,
    config: SimulationConfig,
    freqData: Uint8Array,
    persistentSpectrumBoost: Float32Array,
    persistentSpectrumTarget: Float32Array,
    balls: Ball[]
  ) {
    const hasAudio = freqData && freqData.length > 0;
    const growRate = config.rhythmicSpectrumGrowRate || 0.1;
    const shrinkRate = config.rhythmicSpectrumShrinkRate || 0.05;
    const timeFactor = deltaTime / 16.67;
    const audioSens = config.rhythmicSpectrumAudioSensitivity ?? 1.0;
    const spatiality = config.rhythmicSpectrumAudioSpatiality ?? 0.0;

    for (let i = 0; i < 256; i++) {
        let audioTarget = 0;
        if (hasAudio) {
            const binIndex = Math.floor((i / 256) * freqData.length);
            const audioVal = (freqData[binIndex] / 255) * audioSens;
            audioTarget = Math.pow(audioVal, 0.25) * 3.0;

            if (spatiality > 0) {
                const barAngle = (i / 256) * Math.PI * 2 - Math.PI;
                let minAngleDiff = Math.PI;
                
                balls.forEach(b => {
                    const ballAngle = Math.atan2(b.pos.y, b.pos.x);
                    let diff = Math.abs(barAngle - ballAngle);
                    if (diff > Math.PI) diff = 2 * Math.PI - diff;
                    if (diff < minAngleDiff) minAngleDiff = diff;
                });

                const spatialFactor = Math.max(1 - spatiality, Math.pow(Math.cos(Math.min(Math.PI/2, minAngleDiff * 1.5)), 2));
                audioTarget *= spatialFactor;
            }
        }

        const collisionTarget = persistentSpectrumTarget[i];
        const collisionActivity = config.rhythmicSpectrumGlobalAudio ? 1.0 : Math.min(1.0, collisionTarget * 2.0);
        const gatedAudioTarget = audioTarget * collisionActivity;
        
        const finalTarget = Math.max(gatedAudioTarget, collisionTarget);
        const current = persistentSpectrumBoost[i];
        
        if (finalTarget > current) {
            const step = (finalTarget - current) * growRate * timeFactor;
            persistentSpectrumBoost[i] = Math.min(finalTarget, current + Math.max(0.01 * timeFactor, step));
        } else if (finalTarget < current) {
            const step = (current - finalTarget) * shrinkRate * timeFactor;
            persistentSpectrumBoost[i] = Math.max(finalTarget, current - Math.max(0.005 * timeFactor, step));
        }

        // Target decay (how long the collision "pulse" stays in the target buffer)
        // Duration from config is in ms. We want it to decay such that it lasts roughly that long.
        // If duration is 1000ms, it should take ~60 ticks to decay.
        // Formula: target *= Math.pow(decayFactor, timeFactor)
        // With duration D, we want target to reach 0.01 in D ms.
        // 0.01 = target_initial * decayFactor^(D / 16.67)
        // decayFactor = (0.01 / initial)^(1 / (D / 16.67))
        // Assuming initial = 1.0, decayFactor = 0.01^(16.67 / D)
        const duration = config.rhythmicSpectrumDuration || 800;
        const decayFactor = Math.pow(0.01, 16.67 / duration);
        
        persistentSpectrumTarget[i] *= Math.pow(decayFactor, timeFactor);
        if (persistentSpectrumTarget[i] < 0.01) persistentSpectrumTarget[i] = 0;
    }
  }
}
